import React from 'react';
import classNames from 'classnames';
import useAlertsIndicator from './useAlertsIndicator';
import useAlertCodeHandler from 'hooks/useAlertCodeHandler';

// @material-ui/core components
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import Grow from '@material-ui/core/Grow';
import Hidden from '@material-ui/core/Hidden';
import Popper from '@material-ui/core/Popper';

// @material-ui/icons
import ErrorIcon from '@material-ui/icons/Error';

// core components
import Button from 'components/CustomButtons/Button';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

const getTruncatedText = (text = '') => {
	const MAX_TEXT_LENGTH = 67;
	return text.length >= MAX_TEXT_LENGTH
		? text.slice(0, MAX_TEXT_LENGTH - 3) + '...'
		: text;
};

export default function AlertsIndicator() {
	const {
		alerts,
		openAlert,
		alertsCount,
		classes,
		loading,
		handleClickAlert,
		toAlertPage,
		markAsRead,
		handleCloseAlert,
		getMarkButtonClasses,
	} = useAlertsIndicator();

	const { resolveAlertLink, resolveAlertMessage } = useAlertCodeHandler(handleCloseAlert);

	const emptyMessage = alerts.length > 0
		? null
		: <div className={classes.menuItem}>You do not have any unread notifications</div>;

	const loadingIndicator = loading
		? <div className={classes.loadingIndicatorWrapper}>
			<div className={classes.loadingIndicator}>
				<LoadingIndicator />
			</div>
		</div>
		: null;

	return (
		<>
			<Button
				color="transparent"
				justIcon
				aria-label="Alerts"
				aria-owns={openAlert ? "alert-menu-list" : null}
				aria-haspopup="true"
				onClick={handleClickAlert}
				className={classes.buttonLink}
			>
				<ErrorIcon
					className={classes.headerLinksSvg}
				/>
				{
					alertsCount > 0
						? <span className={classes.notifications}>{alertsCount}</span>
						: null
				}
				<Hidden mdUp implementation="css">
					<span
						onClick={handleClickAlert}
						className={classes.linkText}
					>
						Alerts
					</span>
				</Hidden>
			</Button>
			<Popper
				open={Boolean(openAlert)}
				anchorEl={openAlert}
				transition
				disablePortal
				placement="bottom-end"
				className={classNames({
					[classes.popperClose]: !openAlert,
					[classes.popperResponsive]: true,
					[classes.popperNav]: true
				})}
			>
				{({ TransitionProps }) => (
					<Grow
						{...TransitionProps}
						id="alert-menu-list"
						style={{ transformOrigin: "0 0 0" }}
					>
						<Paper className={classes.dropdown}>
							{loadingIndicator}
							<ClickAwayListener onClickAway={handleCloseAlert}>
								<MenuList role="menu">
									{
										alerts.map((alertItem) => {
											return (
												<div
													key={alertItem.id}
													className={classes.menuItem}
													onClick={() => resolveAlertLink(alertItem)}
												>
													<span className={classes.menuItemText}>
														{getTruncatedText(resolveAlertMessage(alertItem.code, alertItem.text, alertItem.payload))}
													</span>
													<Button
														size="sm"
														onClick={(event) => markAsRead(event, alertItem)}
														className={getMarkButtonClasses(alertItem.isRead)}
													>
														{alertItem.isRead ? 'Mark as unread' : 'Mark as read'}
													</Button>
												</div>
											)
										})
									}
									{emptyMessage}
									<MenuItem
										onClick={toAlertPage}
										className={classes.viewAll}
									>
										View all
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
}
